import { api } from '@/services/api'
import type {
  BookAccess,
  Highlight,
  HighlightCreate,
  LibraryCollection,
  Page,
  ReadingProgress,
} from '@/types/api'

export async function fetchLibrary(): Promise<LibraryCollection[]> {
  // Envelope {items, page_info} do ADR-006; a biblioteca de um aluno cabe numa
  // página só, então o `page_info` é desembrulhado aqui e a view não muda.
  const { data } = await api.get<Page<LibraryCollection>>('/reader/library')
  return data.items
}

export interface StudentDashboard {
  books: number
  available: number
  brand: { name: string; primary_color: string }
  continue_reading: { item: import('@/types/api').LibraryItem; page: number | null } | null
}

export async function fetchDashboard(): Promise<StudentDashboard> {
  const { data } = await api.get<StudentDashboard>('/reader/dashboard')
  return data
}

export async function fetchAccess(assetId: string): Promise<BookAccess> {
  const { data } = await api.get<BookAccess>(`/reader/books/${assetId}/access`)
  return data
}

export async function fetchProgress(bookId: string): Promise<ReadingProgress> {
  const { data } = await api.get<ReadingProgress>(`/reader/progress/${bookId}`)
  return data
}

export interface ProgressPayload {
  book_id: string
  locator?: string | null
  page?: number | null
  scroll_pct?: number | null
}

export async function saveProgress(payload: ProgressPayload): Promise<ReadingProgress> {
  const { data } = await api.post<ReadingProgress>('/reader/progress', {
    ...payload,
    // O carimbo do CLIENTE é o que ordena o last-write-wins no servidor (N19).
    client_updated_at: new Date().toISOString(),
  })
  return data
}

// --- destaques (Fase 03 US-05 / Fase 10 US-05) -------------------------------

/** Busca TODOS os destaques do livro, seguindo o cursor até o fim.
 *
 * ⚠️ Paginar aqui não é zelo: o endpoint devolve keyset (`page_info`), e parar
 * na primeira página faria destaques sumirem da tela do aluno que marca muito —
 * justamente quem mais usa o recurso.
 */
export async function fetchHighlights(bookId: string): Promise<Highlight[]> {
  const todos: Highlight[] = []
  let cursor: string | null = null

  do {
    const { data }: { data: Page<Highlight> } = await api.get<Page<Highlight>>(
      `/reader/highlights/${bookId}`,
      { params: { limit: 100, ...(cursor ? { cursor } : {}) } },
    )
    todos.push(...data.items)
    cursor = data.page_info.has_more ? data.page_info.next_cursor : null
  } while (cursor)

  return todos
}

export async function createHighlight(payload: HighlightCreate): Promise<Highlight> {
  const { data } = await api.post<Highlight>('/reader/highlights', payload)
  return data
}

export async function deleteHighlight(highlightId: string): Promise<void> {
  await api.delete(`/reader/highlights/${highlightId}`)
}
